'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';

const FEATURES = [
  {
    title: 'Flexible Schedule',
    body: 'Choose weekday, weekend, or both. Work the events that fit your life.',
    accent: '#E8B4C8',
  },
  {
    title: 'Paid Events',
    body: 'Competitive pay for every field event. No unpaid training or hidden costs.',
    accent: '#C9A96E',
  },
  {
    title: 'National Reach',
    body: 'Events in 48 markets. Work locally or travel for premium activations.',
    accent: '#B8D4C8',
  },
  {
    title: 'Real Agency',
    body: 'Get Ahead is a professional agency, not a gig app. Support, structure, growth.',
    accent: '#D4B8D4',
  },
];

export default function GetAheadLandingPage() {
  const [scrollY, setScrollY] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      className="min-h-screen overflow-x-hidden"
      style={{ background: '#FAFAF8', fontFamily: '"DM Sans", system-ui, sans-serif', color: '#1a1a1a' }}
    >
      {/* ── NAV ── */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 md:px-14 py-5 transition-all duration-500"
        style={{
          background: mounted && scrollY > 60 ? 'rgba(250,250,248,0.94)' : 'transparent',
          backdropFilter: mounted && scrollY > 60 ? 'blur(20px)' : 'none',
          borderBottom: mounted && scrollY > 60 ? '1px solid rgba(0,0,0,0.06)' : 'none',
        }}
      >
        <div className="flex items-center gap-3">
          <span className="font-black text-xl tracking-tight" style={{ letterSpacing: '-0.04em', color: '#1a1a1a' }}>
            Get Ahead
          </span>
          <span
            className="text-[9px] px-2.5 py-1 rounded-full font-bold hidden sm:block tracking-[0.18em] uppercase"
            style={{ background: '#F5E6EE', color: '#C4688A', border: '1px solid #EAC8D8' }}
          >
            Agency
          </span>
        </div>
        <div className="hidden md:flex items-center gap-10 text-sm font-medium" style={{ color: '#888' }}>
          <a href="#why" className="hover:text-black transition-colors duration-200">Why Us</a>
          <a href="#results" className="hover:text-black transition-colors duration-200">Models</a>
          <a href="#apply" className="hover:text-black transition-colors duration-200">Apply</a>
        </div>
        <Link
          href="/job-interest"
          className="text-sm font-bold px-6 py-2.5 rounded-full transition-all duration-200 hover:scale-105 active:scale-95"
          style={{ background: '#1a1a1a', color: '#fff', letterSpacing: '-0.01em' }}
        >
          Apply Now
        </Link>
      </nav>
      {/* ── HERO ── */}
      <section className="relative min-h-screen flex items-end overflow-hidden" style={{ background: '#F7F0F3' }}>
        {/* Soft blush gradient background */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 70% 80% at 65% 40%, rgba(232,180,200,0.28) 0%, rgba(212,184,212,0.14) 50%, transparent 80%)',
          }}
        />

        {/* Hero image — right side */}
        <div className="absolute right-0 top-0 bottom-0 w-full md:w-[55%] overflow-hidden">
          <Image
            src="/assets/images/promodelle_hero.png"
            alt="Glamorous promotional model in branded uniform at a live sporting event stadium with arena lights"
            fill
            className="object-cover object-top"
            priority
          />
          {/* Fade left edge into background */}
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(to right, #F7F0F3 0%, rgba(247,240,243,0.7) 25%, rgba(247,240,243,0.1) 55%, transparent 100%)',
            }}
          />
          {/* Fade bottom */}
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(to top, #F7F0F3 0%, rgba(247,240,243,0.4) 20%, transparent 50%)',
            }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-14 w-full pb-24 pt-36">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-8 h-px" style={{ background: '#C4688A' }} />
            <span
              className="text-[10px] font-bold tracking-[0.25em] uppercase"
              style={{ color: '#C4688A' }}
            >
              Promodelle Agency
            </span>
          </div>

          {/* Headline */}
          <h1
            className="font-black leading-[0.9] tracking-tight mb-7"
            style={{ fontSize: 'clamp(52px, 8.5vw, 118px)', letterSpacing: '-0.04em', maxWidth: '680px' }}
          >
            <span className="block" style={{ color: '#1a1a1a' }}>Your Stage.</span>
            <span
              className="block"
              style={{
                background: 'linear-gradient(135deg, #C4688A 0%, #A0507A 50%, #C9A96E 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Our Reach.
            </span>
          </h1>

          <p
            className="text-lg md:text-xl leading-relaxed mb-10"
            style={{ color: '#666', maxWidth: '480px', fontWeight: 400 }}
          >
            Join a real agency with flexible schedules, paid field events, and national
            opportunities. Get Ahead connects confident individuals to premium brand
            activations across the country.
          </p>

          <div className="flex flex-wrap items-center gap-5 mb-14">
            <Link
              href="/job-interest"
              className="group inline-flex items-center gap-3 px-9 py-4 rounded-full font-bold text-base transition-all duration-300 hover:scale-105 active:scale-95"
              style={{
                background: '#1a1a1a',
                color: '#fff',
                boxShadow: '0 8px 40px rgba(26,26,26,0.18)',
              }}
            >
              Apply Now
              <svg className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </Link>
            <span className="text-sm" style={{ color: '#aaa' }}>
              No experience required · Paid training · Nationwide events
            </span>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap gap-10 md:gap-16">
            {[
              { value: '500+', label: 'Events Annually' },
              { value: '48', label: 'Markets' },
              { value: '24hr', label: 'Response Time' },
            ]?.map((s) => (
              <div key={s?.label}>
                <p
                  className="font-black leading-none mb-1"
                  style={{ fontSize: 'clamp(32px, 4vw, 52px)', letterSpacing: '-0.04em', color: '#1a1a1a' }}
                >
                  {s?.value}
                </p>
                <p className="text-xs font-semibold tracking-[0.15em] uppercase" style={{ color: '#aaa' }}>
                  {s?.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ── MARQUEE STRIP ── */}
      <div
        className="relative overflow-hidden py-4 border-y"
        style={{ background: '#1a1a1a', borderColor: '#1a1a1a' }}
      >
        <div
          className="flex gap-12 whitespace-nowrap"
          style={{ animation: 'marquee 30s linear infinite' }}
        >
          {Array(3)?.fill(null)?.map((_, i) => (
            <React.Fragment key={i}>
              {[
                'Premium Brand Activations',
                '48 Markets Nationwide',
                'Paid Field Events',
                'Flexible Scheduling',
                'No Experience Required',
                'Real Agency Support',
                'National Opportunities',
                'Paid Training',
              ]?.map((item) => (
                <span
                  key={item}
                  className="text-[10px] font-bold tracking-[0.22em] uppercase inline-flex items-center gap-4"
                  style={{ color: 'rgba(255,255,255,0.55)' }}
                >
                  {item}
                  <span style={{ color: '#C4688A' }}>✦</span>
                </span>
              ))}
            </React.Fragment>
          ))}
        </div>
        <style>{`@keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-33.333%); } }`}</style>
      </div>
      {/* ── WHY GET AHEAD ── */}
      <section id="why" className="py-28 md:py-36" style={{ background: '#FAFAF8' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-14">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-6 h-px" style={{ background: '#C4688A' }} />
                <span className="text-[10px] font-bold tracking-[0.25em] uppercase" style={{ color: '#C4688A' }}>
                  Why Get Ahead
                </span>
              </div>
              <h2
                className="font-black leading-[0.92] tracking-tight"
                style={{ fontSize: 'clamp(36px, 5.5vw, 76px)', letterSpacing: '-0.04em' }}
              >
                Built for
                <br />
                <span style={{ color: '#C4688A' }}>Ambitious</span>
                <br />
                Professionals.
              </h2>
            </div>
            <p className="text-base leading-relaxed md:max-w-xs" style={{ color: '#888' }}>
              We're not a gig app. We're a real agency that invests in your success from day one.
            </p>
          </div>

          {/* Asymmetric bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {/* Large card */}
            <div
              className="md:col-span-5 md:row-span-2 rounded-3xl p-10 flex flex-col justify-between min-h-[320px] relative overflow-hidden group"
              style={{ background: '#F5E6EE', border: '1px solid #EAD0DC' }}
            >
              <div>
                <div
                  className="w-10 h-10 rounded-2xl mb-8 flex items-center justify-center text-lg"
                  style={{ background: '#EAC8D8' }}
                >
                  ◷
                </div>
                <h3
                  className="font-black text-2xl md:text-3xl mb-4 leading-tight"
                  style={{ letterSpacing: '-0.03em', color: '#1a1a1a' }}
                >
                  {FEATURES?.[0]?.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: '#888' }}>
                  {FEATURES?.[0]?.body}
                </p>
              </div>
              <div
                className="mt-8 text-xs font-bold tracking-[0.15em] uppercase"
                style={{ color: '#C4688A' }}
              >
                Your schedule →
              </div>
            </div>

            {/* Top right wide */}
            <div
              className="md:col-span-7 rounded-3xl p-8 flex flex-col justify-between min-h-[200px] relative overflow-hidden"
              style={{ background: '#FDF8F0', border: '1px solid #EDE0CC' }}
            >
              <div
                className="w-10 h-10 rounded-2xl mb-6 flex items-center justify-center text-lg"
                style={{ background: '#EDD8B0' }}
              >
                ◈
              </div>
              <div>
                <h3
                  className="font-black text-2xl mb-3 leading-tight"
                  style={{ letterSpacing: '-0.03em', color: '#1a1a1a' }}
                >
                  {FEATURES?.[1]?.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: '#888' }}>
                  {FEATURES?.[1]?.body}
                </p>
              </div>
            </div>

            {/* Bottom right — two cards */}
            <div
              className="md:col-span-4 rounded-3xl p-8 flex flex-col justify-between min-h-[180px]"
              style={{ background: '#EEF5F1', border: '1px solid #CCE0D4' }}
            >
              <div
                className="w-10 h-10 rounded-2xl mb-6 flex items-center justify-center text-lg"
                style={{ background: '#C4DDD0' }}
              >
                ◉
              </div>
              <div>
                <h3
                  className="font-black text-xl mb-2 leading-tight"
                  style={{ letterSpacing: '-0.03em', color: '#1a1a1a' }}
                >
                  {FEATURES?.[2]?.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: '#888' }}>
                  {FEATURES?.[2]?.body}
                </p>
              </div>
            </div>

            <div
              className="md:col-span-3 rounded-3xl p-8 flex flex-col justify-between min-h-[180px]"
              style={{ background: '#F5EEF8', border: '1px solid #DDD0E8' }}
            >
              <div
                className="w-10 h-10 rounded-2xl mb-6 flex items-center justify-center text-lg"
                style={{ background: '#D4B8D4' }}
              >
                ◆
              </div>
              <div>
                <h3
                  className="font-black text-xl mb-2 leading-tight"
                  style={{ letterSpacing: '-0.03em', color: '#1a1a1a' }}
                >
                  {FEATURES?.[3]?.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: '#888' }}>
                  {FEATURES?.[3]?.body}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ── REAL RESULTS / TESTIMONIAL ── */}
      <section
        id="results"
        className="py-28 md:py-36 relative overflow-hidden"
        style={{ background: '#1a1a1a' }}
      >
        {/* Subtle blush glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 60% 50% at 30% 50%, rgba(196,104,138,0.12), transparent 70%)',
          }}
        />

        <div className="max-w-7xl mx-auto px-6 md:px-14 relative z-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-6 h-px" style={{ background: '#C4688A' }} />
            <span className="text-[10px] font-bold tracking-[0.25em] uppercase" style={{ color: '#C4688A' }}>
              Real Results
            </span>
          </div>
          <h2
            className="font-black leading-[0.92] tracking-tight mb-16 text-white"
            style={{ fontSize: 'clamp(36px, 5.5vw, 76px)', letterSpacing: '-0.04em' }}
          >
            Models Are Building
            <br />
            Real Careers.
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            {/* Testimonial */}
            <div
              className="rounded-3xl p-10 flex flex-col justify-between relative overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <div
                className="absolute top-0 left-0 right-0 h-0.5 rounded-t-3xl"
                style={{ background: 'linear-gradient(to right, #C4688A, #C9A96E)' }}
              />
              <div>
                <p
                  className="text-xs font-bold tracking-[0.2em] uppercase mb-8"
                  style={{ color: '#C4688A' }}
                >
                  Flexible income from premium brand activations.
                </p>
                <blockquote
                  className="font-semibold text-xl md:text-2xl leading-snug mb-10"
                  style={{ letterSpacing: '-0.01em', color: 'rgba(255,255,255,0.88)' }}
                >
                  "Started as a side gig. Now I turn down events. The MensConfidenceStrategies
                  mission actually matters to me."
                </blockquote>
              </div>
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center font-black text-sm shrink-0"
                  style={{ background: 'linear-gradient(135deg, #C4688A, #A0507A)', color: '#fff' }}
                >
                  SC
                </div>
                <div>
                  <p className="font-bold text-sm text-white">Sofia Chen</p>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Event Model</p>
                </div>
              </div>
            </div>

            {/* Image panel */}
            <div className="relative rounded-3xl overflow-hidden" style={{ minHeight: '360px' }}>
              <Image
                src="/assets/images/promodelle_team.png"
                alt="Diverse promotional models in event uniforms engaging with fans at an outdoor sporting event"
                fill
                className="object-cover object-center"
              />
              <div
                className="absolute inset-0"
                style={{ background: 'linear-gradient(to top, rgba(26,26,26,0.7) 0%, transparent 55%)' }}
              />
              <div
                className="absolute bottom-6 left-6 right-6 rounded-2xl px-5 py-4"
                style={{
                  background: 'rgba(26,26,26,0.82)',
                  backdropFilter: 'blur(16px)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <p className="font-black text-sm mb-0.5" style={{ color: '#C4688A' }}>
                  Premium Activations
                </p>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  Where Confidence Meets Opportunity
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ── MORE THAN A SIDE GIG ── */}
      <section className="py-28 md:py-36" style={{ background: '#FAFAF8' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-6 h-px" style={{ background: '#C4688A' }} />
                <span className="text-[10px] font-bold tracking-[0.25em] uppercase" style={{ color: '#C4688A' }}>
                  More Than a Side Gig
                </span>
              </div>
              <h2
                className="font-black leading-[0.92] tracking-tight mb-8"
                style={{ fontSize: 'clamp(36px, 5vw, 68px)', letterSpacing: '-0.04em' }}
              >
                Where Confidence
                <br />
                <span
                  style={{
                    background: 'linear-gradient(135deg, #C4688A 0%, #A0507A 50%, #C9A96E 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Meets Opportunity
                </span>
              </h2>
              <p className="text-base leading-relaxed mb-10" style={{ color: '#888', maxWidth: '440px' }}>
                Get Ahead partners with premium brands for field marketing events powered by
                the MensConfidenceStrategies.org mission. Every event is an opportunity to make
                an impact, build your portfolio, and earn.
              </p>
              <Link
                href="/job-interest"
                id="apply"
                className="group inline-flex items-center gap-3 px-9 py-4 rounded-full font-bold text-base transition-all duration-300 hover:scale-105 active:scale-95"
                style={{
                  background: 'linear-gradient(135deg, #C4688A, #A0507A)',
                  color: '#fff',
                  boxShadow: '0 8px 40px rgba(196,104,138,0.35)',
                }}
              >
                Start Your Application
                <svg className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
            </div>

            {/* Stat cards — light pastel */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '500+', label: 'Events Annually', sub: 'Across all 48 markets', bg: '#F5E6EE', accent: '#C4688A' },
                { value: '48', label: 'Markets', sub: 'Work local or travel', bg: '#FDF8F0', accent: '#C9A96E' },
                { value: '$0', label: 'Hidden Costs', sub: 'No unpaid training', bg: '#EEF5F1', accent: '#5A9E7A' },
                { value: '24hr', label: 'Response Time', sub: 'Real agency support', bg: '#F5EEF8', accent: '#9A6AAA' },
              ]?.map((item) => (
                <div
                  key={item?.label}
                  className="rounded-2xl p-6 flex flex-col gap-2 group hover:scale-[1.02] transition-transform duration-200"
                  style={{ background: item?.bg, border: '1px solid rgba(0,0,0,0.05)' }}
                >
                  <p
                    className="font-black text-3xl leading-none"
                    style={{ letterSpacing: '-0.04em', color: item?.accent }}
                  >
                    {item?.value}
                  </p>
                  <p className="font-bold text-sm" style={{ color: '#1a1a1a' }}>{item?.label}</p>
                  <p className="text-xs" style={{ color: '#aaa' }}>{item?.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* ── FINAL CTA ── */}
      <section
        id="apply-cta"
        className="relative py-28 md:py-40 overflow-hidden"
        style={{ background: '#F7F0F3' }}
      >
        {/* Decorative blush orbs */}
        <div
          className="absolute top-0 right-0 w-96 h-96 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(196,104,138,0.18) 0%, transparent 70%)',
            filter: 'blur(60px)',
            transform: 'translate(30%, -30%)',
          }}
        />
        <div
          className="absolute bottom-0 left-0 w-80 h-80 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(201,169,110,0.14) 0%, transparent 70%)',
            filter: 'blur(60px)',
            transform: 'translate(-30%, 30%)',
          }}
        />

        {/* Ghost text */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          <span
            className="font-black select-none whitespace-nowrap"
            style={{
              fontSize: 'clamp(60px, 14vw, 200px)',
              color: 'transparent',
              WebkitTextStroke: '1px rgba(196,104,138,0.08)',
              letterSpacing: '-0.04em',
            }}
          >
            GET AHEAD
          </span>
        </div>

        <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-14 text-center">
          <div className="flex items-center justify-center gap-3 mb-7">
            <div className="w-6 h-px" style={{ background: '#C4688A' }} />
            <span className="text-[10px] font-bold tracking-[0.25em] uppercase" style={{ color: '#C4688A' }}>
              Ready to Join?
            </span>
            <div className="w-6 h-px" style={{ background: '#C4688A' }} />
          </div>
          <h2
            className="font-black leading-[0.9] tracking-tight mb-8"
            style={{ fontSize: 'clamp(40px, 7vw, 96px)', letterSpacing: '-0.04em', color: '#1a1a1a' }}
          >
            Your Stage.
            <br />
            <span
              style={{
                background: 'linear-gradient(135deg, #C4688A 0%, #A0507A 50%, #C9A96E 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Our Reach.
            </span>
          </h2>
          <p className="text-base mb-10" style={{ color: '#aaa' }}>
            No experience required · Paid training · Nationwide events
          </p>
          <Link
            href="/job-interest"
            className="group inline-flex items-center gap-3 px-10 py-5 rounded-full font-bold text-lg transition-all duration-300 hover:scale-105 active:scale-95"
            style={{
              background: '#1a1a1a',
              color: '#fff',
              boxShadow: '0 12px 50px rgba(26,26,26,0.2)',
            }}
          >
            Apply Now
            <svg className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </Link>
        </div>
      </section>
      {/* ── FOOTER ── */}
      <footer
        className="py-10 px-6 md:px-14 flex flex-col md:flex-row items-center justify-between gap-4"
        style={{ borderTop: '1px solid rgba(0,0,0,0.07)', background: '#FAFAF8' }}
      >
        <span className="font-black text-base" style={{ letterSpacing: '-0.03em', color: '#1a1a1a' }}>
          Get Ahead
        </span>
        <p className="text-xs" style={{ color: '#bbb' }}>
          © {new Date()?.getFullYear()} Get Ahead Agency. All rights reserved.
        </p>
        <Link
          href="/barber-landing"
          className="text-xs transition-colors duration-200 hover:text-black"
          style={{ color: '#bbb' }}
        >
          For Barbers →
        </Link>
      </footer>
    </div>
  );
}
